import { createI18n } from 'vue-i18n'
import { defaultLocale, detectLocale, getLocaleConfig, supportedLocaleCodes } from './config'

const loadedFeatures = new Set<string>()

function featureKey(locale: string, feature: string): string {
  return `${locale}:${feature}`
}

export const i18n = createI18n({
  legacy: false,
  locale: defaultLocale,
  fallbackLocale: false,
  missingWarn: false,
  fallbackWarn: false,
})

export async function loadCommonMessages(locale: string): Promise<void> {
  const key = featureKey(locale, 'common')
  if (loadedFeatures.has(key)) return

  const messages = await import(`./${locale}/common.json`)
  i18n.global.mergeLocaleMessage(locale, messages.default)
  loadedFeatures.add(key)
}

export async function loadFeatureMessages(locale: string, feature: string): Promise<void> {
  const key = featureKey(locale, feature)
  if (loadedFeatures.has(key)) return

  try {
    const messages = await import(`./${locale}/${feature}.json`)
    i18n.global.mergeLocaleMessage(locale, messages.default)
    loadedFeatures.add(key)
  } catch {
    // Feature file doesn't exist yet — skip silently
  }
}

export async function setI18nLocale(locale: string): Promise<void> {
  if (!supportedLocaleCodes.includes(locale)) return

  await loadCommonMessages(locale)
  i18n.global.locale.value = locale
  localStorage.setItem('user-locale', locale)

  const config = getLocaleConfig(locale)
  document.documentElement.lang = locale
  document.documentElement.dir = config.dir
}

export async function initI18n(): Promise<string> {
  const locale = detectLocale()
  await setI18nLocale(locale)
  return locale
}

/** Map route path to feature translation file(s) */
export function getRouteFeatures(path: string): string[] {
  const clean = path.replace(/^\/[a-z]{2}\//, '/')

  if (clean.startsWith('/challenges') || clean.startsWith('/create-challenge')) return ['challenge', 'support']
  if (clean.startsWith('/profile')) return ['profile', 'support', 'pricing']
  if (clean.startsWith('/login') || clean.startsWith('/auth/') || clean === '/auth') return ['auth']
  if (clean.startsWith('/leaderboard')) return ['leaderboard']
  if (clean.startsWith('/pricing')) return ['pricing']
  if (clean.startsWith('/admin')) return ['admin', 'support', 'referral']
  if (clean.startsWith('/payment')) return ['payment']
  if (clean.startsWith('/authors')) return ['challenge', 'author']
  if (clean.startsWith('/referral')) return ['referral']
  if (clean.startsWith('/author')) return ['referral', 'author']
  return []
}

export async function loadRouteMessages(locale: string, path: string): Promise<void> {
  const features = getRouteFeatures(path)
  await Promise.all([
    loadCommonMessages(locale),
    ...features.map((f) => loadFeatureMessages(locale, f)),
  ])
}
